import { Link } from 'react-router-dom';
import { Lock, Mail, Video, QrCode, Tablet, Users, Image } from 'lucide-react';
import LandingNavbar from '../components/landing/LandingNavbar';
import KioskDemo from '../components/landing/KioskDemo';
import styles from '../styles/LandingPage.module.css';
import heroBg from '../assets/images/landing/hero-bg.jpg';
import textureBg from '../assets/images/landing/texture-bg.jpg';
import memoryImg from '../assets/images/landing/memory.png';

export default function LandingPage() {
  const scrollToInquiry = (e) => {
    e.preventDefault();
    document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDemo = (e) => {
    e.preventDefault();
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.landingPage}>
      <style>{`
        .${styles.landingPage}::before {
          background-image: url(${textureBg});
        }
      `}</style>

      <LandingNavbar />

      {/* Hero */}
      <header
        className={styles.hero}
        style={{
          background: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.8)), url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        <div>
          <p className={styles.tagline}>MomentBox</p>
          <h1 className={styles.heroTitle}>
            Collect Video Messages<br />From Every Guest.
          </h1>
          <p className={styles.heroDescription}>
            A premium digital video guestbook for your event.
            Guests record heartfelt messages at your venue or from their
            phone — you get a private gallery to relive forever.
          </p>
          <div className={styles.heroCtas}>
            <a href="#inquiry" className={styles.ctaButton} onClick={scrollToInquiry}>
              Inquire Now
            </a>
            <a href="#demo" className={styles.ctaButtonOutline} onClick={scrollToDemo}>
              See It In Action
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* Problem */}
        <section className={styles.problem}>
          <div className={styles.container}>
            <div className={styles.splitLayout}>
              <div>
                <h2 className={styles.sectionTitle} style={{ textAlign: 'left' }}>
                  Your Guests Have Beautiful Things to Say
                </h2>
                <p className={styles.paragraph}>
                  Right now, those moments disappear the second the party ends.
                  A toast that made everyone cry. A friend's inside joke caught
                  on camera. Your love one's voice wishing you well.
                </p>
                <p className={styles.paragraph}>
                  Photos capture the scene — but not the feeling. And no one
                  ever goes back to sign the guestbook.
                </p>
              </div>
              <div className={styles.visualContainer}>
                <img src={memoryImg} alt="Gold Camera Lens" />
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Kiosk Demo */}
        <KioskDemo />

        {/* How It Works */}
        <section>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>How It Works</h2>
            <p className={styles.sectionSubtitle}>
              Three simple steps. No apps, no downloads, no friction.
            </p>

            <div className={styles.stepsGrid}>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepLine} />
                <h3>We Set Up</h3>
                <p>We configure your branded kiosk and provide QR codes. Just place a tablet at your venue.</p>
              </div>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepLine} />
                <h3>Guests Record</h3>
                <p>Guests tap to record video messages or take photos. They can also upload from their phone via QR code.</p>
              </div>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>3</div>
                <h3>You Get a Gallery</h3>
                <p>Access your private gallery to watch, download, and share every message with family and friends.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What You Get */}
        <section className={styles.problem}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>What You Get After Your Event</h2>
            <p className={styles.sectionSubtitle}>
              A private, curated gallery of every video message — ready to watch, share, and keep.
            </p>

            <div className={styles.deliverableGrid}>
              <div className={styles.deliverableCard}>
                <div className={styles.deliverableIcon}>
                  <Lock size={48} strokeWidth={1.5} />
                </div>
                <h3>Password-Protected Gallery</h3>
                <p>Your gallery is private by default. Set a password, or lock it down so only you can access it via email verification.</p>
              </div>
              <div className={styles.deliverableCard}>
                <div className={styles.deliverableIcon}>
                  <Mail size={48} strokeWidth={1.5} />
                </div>
                <h3>Share With One Click</h3>
                <p>Email the gallery link to family and friends in bulk. The password is included automatically — they just click and watch.</p>
              </div>
              <div className={styles.deliverableCard}>
                <div className={styles.deliverableIcon}>
                  <Video size={48} strokeWidth={1.5} />
                </div>
                <h3>HD Video Playback</h3>
                <p>Every message is recorded in 1080p at 30fps. Watch them in a clean video player with guest names and timestamps.</p>
              </div>
              <div className={styles.deliverableCard}>
                <div className={styles.deliverableIcon}>
                  <QrCode size={48} strokeWidth={1.5} />
                </div>
                <h3>QR Codes for Everything</h3>
                <p>Get shareable QR codes for your kiosk, upload page, and gallery. Print them on table cards or display at the venue.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>For Every Kind of Event</h2>
            <p className={styles.sectionSubtitle}>
              Whether it's intimate or large-scale, MomentBox fits.
            </p>

            <div className={styles.featuresGrid}>
              <div className={styles.featureCard}>
                <h3>Weddings &amp; Private</h3>
                <p className={styles.paragraph}>
                  Replace the dusty guestbook with something you'll actually revisit.
                  Collect video toasts, well-wishes, and stories from every table.
                </p>
                <ul>
                  <li>
                    <strong>Video Toasts:</strong>
                    <span>Guests leave messages you'll watch on anniversaries for years to come.</span>
                  </li>
                  <li>
                    <strong>Family Sharing:</strong>
                    <span>Email the gallery to relatives who couldn't attend — they can upload their own messages too.</span>
                  </li>
                </ul>
              </div>
              <div className={styles.featureCard}>
                <h3>Corporate &amp; Brand</h3>
                <p className={styles.paragraph}>
                  Capture authentic testimonials and team messages at galas,
                  offsites, trade shows, or company parties.
                </p>
                <ul>
                  <li>
                    <strong>Testimonials:</strong>
                    <span>Collect genuine, on-the-spot video feedback you can use in marketing.</span>
                  </li>
                  <li>
                    <strong>Team Culture:</strong>
                    <span>Let employees share shout-outs and stories — great for internal comms.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Privacy */}
        <section className={styles.problem}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Built Around Trust</h2>
            <p className={styles.sectionSubtitle}>
              Your guests' messages are personal. We treat them that way.
            </p>

            <div className={styles.trustGrid}>
              <div className={styles.trustItem}>
                <h3>Host-Only Access</h3>
                <p>You control who sees the gallery. Toggle between public, password-protected, or fully private at any time.</p>
              </div>
              <div className={styles.trustItem}>
                <h3>Transparent Consent</h3>
                <p>Every guest sees a clear data privacy notice before recording. They know exactly what's collected and how it's stored.</p>
              </div>
              <div className={styles.trustItem}>
                <h3>Delete Anytime</h3>
                <p>Remove individual videos or your entire event with one click. When it's gone, it's gone — no lingering copies.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Inquire Now */}
        <section id="inquiry">
          <div className={`${styles.container} ${styles.waitlistInner}`}>
            <h2 className={styles.sectionTitle}>Ready to Make Your Event Unforgettable?</h2>
            <p className={styles.paragraph}>
              Tell us about your event and we'll get back to you within 24 hours.
            </p>

            <form
              className={styles.waitlistForm}
              action="https://formspree.io/f/xgvpjdqy"
              method="POST"
            >
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Your full name"
                required
              />

              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                required
              />

              <label htmlFor="event-type">Event Type</label>
              <select id="event-type" name="event-type" required defaultValue="">
                <option value="" disabled>-- Select your event type --</option>
                <option value="wedding">Wedding</option>
                <option value="corporate">Corporate Event</option>
                <option value="gala">Gala / Awards Night</option>
                <option value="private-party">Private Party</option>
                <option value="other">Other</option>
              </select>

              <label htmlFor="event-date">Approximate Event Date (Optional)</label>
              <input
                type="date"
                id="event-date"
                name="event-date"
              />

              <label htmlFor="message">Message / Questions (Optional)</label>
              <textarea
                id="message"
                name="message"
                rows="3"
                placeholder="Tell us about your event, ask questions, or let us know what you need..."
              />

              <button type="submit" className={styles.ctaButton}>
                Send Inquiry
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <p>&copy; 2026 MomentBox. All rights reserved.</p>
        <Link to="/admin/login" style={{ color: '#444', fontSize: '0.75rem', textDecoration: 'none', marginTop: '0.5rem', display: 'inline-block' }}>
          Admin
        </Link>
      </footer>
    </div>
  );
}
